module Data.BasicURL
  ( BasicQueryPairs
  , BasicURL
  , _scheme
  , addPathSegment
  , addQueryParameter
  , addQueryParameterIfNotExists
  , fromString
  , getQueryPairs
  , getQueryParameter
  , make
  , parse
  , parseMany
  , print
  , printMany
  , replacePath
  ) where

import Prelude

import Control.Monad.Gen (elements)
import Data.Argonaut (class DecodeJson, class EncodeJson, JsonDecodeError(..), decodeJson, encodeJson)
import Data.Array as Array
import Data.Array.NonEmpty (NonEmptyArray)
import Data.Array.NonEmpty as NEA
import Data.Bifunctor (lmap)
import Data.Either (Either(..), fromRight', hush)
import Data.Filterable (filter)
import Data.Lens (Lens', lens)
import Data.Lens as Lens
import Data.Maybe (Maybe(..), fromMaybe, fromMaybe')
import Data.String as Str
import Data.These (These(..))
import Data.Traversable (traverse)
import Data.Tuple (Tuple(..), fst)
import Data.Tuple.Nested (type (/\), (/\))
import Parsing (ParseError, Parser, runParser)
import Partial.Unsafe (unsafeCrashWith, unsafePartial)
import Test.QuickCheck (class Arbitrary)
import TypedEnv (class ParseValue)
import URI (AbsoluteURI(..), Authority(..), Host, Path(..), Port, Scheme)
import URI.AbsoluteURI (HierarchicalPart(..), _hierPart, _path, _query)
import URI.AbsoluteURI as AbsoluteURI
import URI.AbsoluteURI as URI
import URI.Common (URIPartParseError(..))
import URI.Extra.QueryPairs (Key, QueryPairs(..), Value, keyFromString, valueToString)
import URI.Extra.QueryPairs as QueryPairs
import URI.Host as Host
import URI.HostPortPair (HostPortPair)
import URI.HostPortPair as HostPortPair
import URI.Path as Path
import URI.Path.Segment (PathSegment)
import URI.Port as Port
import URI.Query as Query
import URI.Scheme as Scheme

--------------------------------------------------------------------------------
--- Types
------------------------------------------------------------------------------

-- | The type of a strongly typed url with queries, no fragments and no user info
-- | The only reason I am using an existing type filled with a bunch of Voids
-- | is to get a pretty printer and a parser for free
newtype BasicURL = BasicURL
  ( AbsoluteURI
      -- user info:
      Void
      -- hosts:
      (HostPortPair Host Port)
      -- path:
      Path
      -- hierarchical path:
      Void
      -- query:
      BasicQueryPairs
  )

type BasicQueryPairs = QueryPairs QueryPairs.Key QueryPairs.Value

--------------------------------------------------------------------------------
--- Constructors
------------------------------------------------------------------------------

make :: Scheme -> Host -> Port -> Path -> Maybe BasicQueryPairs -> BasicURL
make scheme host port path query = BasicURL $
  AbsoluteURI scheme
    ( HierarchicalPartAuth
        ( Authority Nothing
            (Just (Both host port))
        )
        path
    )
    query

fromString :: Partial => String -> BasicURL
fromString str =
  fromRight'
    (\_ -> unsafeCrashWith "invalid url") $ parse str

parse :: String -> Either ParseError BasicURL
parse str = runParser str parser

parseMany :: String -> Maybe (NonEmptyArray BasicURL)
parseMany str = do
  let
    parts = Str.split (Str.Pattern ",") str

  partsParsed <- traverse (parse >>> hush) parts

  NEA.fromArray partsParsed

--------------------------------------------------------------------------------
--- Lenses
------------------------------------------------------------------------------

_scheme :: Lens' BasicURL Scheme
_scheme = lens getter setter <<< URI._scheme
  where
  getter (BasicURL uri) = uri
  setter _ uri = BasicURL uri

--------------------------------------------------------------------------------
--- Destructors
------------------------------------------------------------------------------

print :: BasicURL -> String
print (BasicURL uri) = AbsoluteURI.print
  { printUserInfo: absurd
  , printHosts: HostPortPair.print identity identity
  , printPath: identity
  , printHierPath: absurd
  , printQuery: QueryPairs.print identity identity
  }
  uri

printMany :: NonEmptyArray BasicURL -> String
printMany urls = NEA.toArray urls
  # map print
  # Str.joinWith ","

getQueryPairs :: BasicURL -> BasicQueryPairs
getQueryPairs (BasicURL (AbsoluteURI _ _ queryPairs)) = fromMaybe mempty queryPairs

--------------------------------------------------------------------------------
--- Path Operations
------------------------------------------------------------------------------

replacePath :: Path -> BasicURL -> BasicURL
replacePath path (BasicURL url) = BasicURL $
  Lens.set (_hierPart <<< _path) path url

addPathSegment :: PathSegment -> BasicURL -> BasicURL
addPathSegment newSegment (BasicURL uri) = BasicURL
  let
    addSegment :: Path -> Path
    addSegment (Path segs) = Path (Array.snoc segs newSegment)
  in
    Lens.over (_hierPart <<< _path) addSegment uri

--------------------------------------------------------------------------------
--- Query Operations
------------------------------------------------------------------------------

addQueryParameter :: String -> Maybe String -> BasicURL -> BasicURL
addQueryParameter = addQueryParameterImpl true

addQueryParameterIfNotExists :: String -> Maybe String -> BasicURL -> BasicURL
addQueryParameterIfNotExists = addQueryParameterImpl false

-- gets a value of a query param
-- Nothing means query param was not found
-- Just Nothing means query param was found but it has no associated value, like this: http://one.two/?a&b
-- Just (Just value) delivers the value associated with a query param
getQueryParameter :: String -> BasicURL -> Maybe (Maybe String)
getQueryParameter paramName url =
  let
    key = keyFromString paramName
    QueryPairs arrayPairs = getQueryPairs url

    tuple :: Maybe (Tuple Key (Maybe Value))
    tuple = Array.find (\(Tuple k _) -> k == key) arrayPairs
  in
    map (\(Tuple _ value) -> map valueToString value) tuple

--------------------------------------------------------------------------------
--- Internal Utils
--------------------------------------------------------------------------------

parser :: Parser String BasicURL
parser =
  AbsoluteURI.parser
    { parseUserInfo: const $ Left $ URIPartParseError "User info is not supported"
    , parseHosts: HostPortPair.parser pure pure
    , parsePath: pure
    , parseHierPath: const $ Left $ URIPartParseError "Hier paths are not supported"
    , parseQuery: QueryPairs.parse pure pure
    }
    <#> BasicURL

addQueryParameterImpl :: Boolean -> String -> Maybe String -> BasicURL -> BasicURL
addQueryParameterImpl overwrite key value (BasicURL uri) = BasicURL
  let
    addQuery :: Maybe BasicQueryPairs -> BasicQueryPairs
    addQuery initialQuery =
      let
        QueryPairs queryPairs = fromMaybe mempty initialQuery

        encKey :: Key
        encKey = QueryPairs.keyFromString key

        encValue :: Maybe Value
        encValue = map QueryPairs.valueFromString value

        filtered :: Array (Key /\ Maybe Value)
        filtered = filter (fst >>> (_ /= encKey)) queryPairs

      in
        if (overwrite || (Array.length filtered == Array.length queryPairs)) then
          QueryPairs (filtered <> [ encKey /\ encValue ])
        else
          QueryPairs queryPairs

  in
    Lens.over _query (addQuery >>> Just) uri

--------------------------------------------------------------------------------
--- Instances
--------------------------------------------------------------------------------

derive newtype instance Eq BasicURL
derive newtype instance Ord BasicURL

instance Show BasicURL where
  show url = "BasicURL.fromString " <> show (print url)

instance EncodeJson BasicURL where
  encodeJson url = encodeJson $ print url

instance DecodeJson BasicURL where
  decodeJson json = do
    str :: String <- decodeJson json

    let
      mapError :: ParseError -> JsonDecodeError
      mapError err = TypeMismatch ("Error parsing URL " <> str <> ": " <> show err)

    let
      result :: Either ParseError BasicURL
      result = parse str

    lmap mapError result

instance Arbitrary BasicURL where
  arbitrary =
    let

      schemes :: NonEmptyArray Scheme
      schemes =
        map unsafeMkScheme $
          NEA.cons' "http" [ "https", "ftp" ]

      hosts :: NonEmptyArray Host
      hosts =
        map unsafeMkHost $
          NEA.cons' "example.com" [ "192.168.0.1", "172.16.254.1", "10.0.0.2" ]

      ports :: NonEmptyArray Port
      ports =
        map unsafeMkPort $
          NEA.cons' 80 [ 443, 8080, 8081, 8082, 8083, 8084, 8085, 8086 ]

      paths :: NonEmptyArray Path
      paths =
        map unsafeMkPath $
          NEA.cons' "/" [ "/foo", "/foo/bar", "/foo/bar/baz" ]

      queries :: NonEmptyArray BasicQueryPairs
      queries =
        map unsafeMkQuery $
          NEA.cons' "?" [ "?foo=bar", "?foo=bar&baz=qux" ]
    in
      ado
        scheme :: Scheme <- elements schemes
        host :: Host <- elements hosts
        port :: Port <- elements ports
        path :: Path <- elements paths
        query :: Maybe BasicQueryPairs <- elements (Nothing `NEA.cons` map Just queries)
        in
          make scheme host port path query

    where

    unsafeMkScheme :: String -> Scheme
    unsafeMkScheme str = unsafePartial $ Scheme.fromString str #
      fromMaybe' (\_ -> unsafeCrashWith ("invalid scheme: " <> str))

    unsafeMkHost :: String -> Host
    unsafeMkHost str = unsafePartial $ runParser str Host.parser #
      fromRight' (\_ -> unsafeCrashWith ("invalid host: " <> str))

    unsafeMkPort :: Int -> Port
    unsafeMkPort str = unsafePartial $ Port.fromInt str #
      fromMaybe' (\_ -> unsafeCrashWith ("invalid port: " <> show str))

    unsafeMkPath :: String -> Path
    unsafeMkPath str = unsafePartial $ (runParser str Path.parser) #
      fromRight' (\_ -> unsafeCrashWith ("invalid path: " <> str))

    unsafeMkQuery :: String -> BasicQueryPairs
    unsafeMkQuery str = unsafePartial $ (Query.fromString >>> QueryPairs.parse pure pure) str #
      fromRight' (\_ -> unsafeCrashWith ("invalid query: " <> str))

instance ParseValue BasicURL where
  parseValue :: String -> Maybe BasicURL
  parseValue = parse >>> hush